import { Children, createContext, useContext, type ReactNode } from 'react';

import { cn } from '@mds/shared/lib/utils';

/** The column labels, for a cell to name itself on a phone. */
const HeadContext = createContext<ReactNode[]>([]);
/** The label of the column a cell sits in; empty for an unlabelled column. */
const LabelContext = createContext<string>('');

/**
 * A horizontally scrollable table. `head` supplies the column labels — usually
 * text, or a control such as a select-all checkbox. `dense` steps the body text
 * down and tightens the rows, for a table with many columns.
 *
 * Below `sm` there is no room for columns: each row becomes a card and each
 * cell is labelled with its text heading. A heading that is a control rather
 * than text sits above the cards instead.
 */
export function Table({
  head,
  dense,
  children,
}: {
  head: ReactNode[];
  dense?: boolean;
  children: ReactNode;
}) {
  const controls = head.filter((h) => h !== null && typeof h === 'object');
  return (
    <HeadContext.Provider value={head}>
      {controls.length > 0 && (
        <div className="mb-3 flex items-center gap-3 sm:hidden">{controls}</div>
      )}
      <div className="sm:overflow-x-auto">
        <table
          className={cn(
            'w-full border-collapse text-left text-base max-sm:block sm:min-w-[720px]',
            dense && 'sm:text-sm sm:[&_td]:py-3 sm:[&_td]:pr-6 sm:[&_th]:pr-6 sm:[&_th]:text-xs',
          )}
        >
          <thead className="max-sm:hidden">
            <tr>
              {head.map((h, i) => (
                <th
                  key={i}
                  className="border-b-2 border-hairline pb-3 font-mono text-sm font-semibold tracking-wide text-ink-secondary uppercase"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="max-sm:flex max-sm:flex-col max-sm:gap-3">{children}</tbody>
        </table>
      </div>
    </HeadContext.Provider>
  );
}

/**
 * One table row. Passing `onClick` also makes it look and behave clickable;
 * `selected` tints it, for a row ticked for a bulk action.
 */
export function Row({
  children,
  onClick,
  selected,
}: {
  children: ReactNode;
  onClick?: () => void;
  selected?: boolean;
}) {
  const head = useContext(HeadContext);
  return (
    <tr
      onClick={onClick}
      className={cn(
        'border-b border-hairline/70 transition-colors',
        'max-sm:flex max-sm:flex-col max-sm:gap-1 max-sm:rounded-2xl max-sm:border max-sm:border-hairline max-sm:px-4 max-sm:py-3',
        onClick && 'cursor-pointer hover:bg-brand-wash',
        selected && 'bg-brand-wash',
      )}
    >
      {/* Cells left out by a condition are dropped here, as their headings are. */}
      {Children.toArray(children).map((cell, i) => (
        <LabelContext.Provider key={i} value={typeof head[i] === 'string' ? head[i] : ''}>
          {cell}
        </LabelContext.Provider>
      ))}
    </tr>
  );
}

/** One table cell. `className` is merged onto the shared padding and alignment. */
export function Cell({ children, className }: { children: ReactNode; className?: string }) {
  const label = useContext(LabelContext);
  return (
    <td
      className={cn(
        'py-4 pr-4 align-middle',
        className,
        'max-sm:py-1 max-sm:pr-0 max-sm:text-left max-sm:whitespace-normal',
        label && 'max-sm:grid max-sm:grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)] max-sm:gap-3',
      )}
    >
      {label ? (
        <>
          <span
            aria-hidden="true"
            className="pt-0.5 font-mono text-xs font-semibold tracking-wide text-ink-secondary uppercase sm:hidden"
          >
            {label}
          </span>
          {/* Laid out as if it were not there on a wider screen. */}
          <div className="min-w-0 break-words sm:contents">{children}</div>
        </>
      ) : (
        children
      )}
    </td>
  );
}
